import React, { useState } from 'react';
import './LandingPage.css';
import CountrySelectionModal from './CountrySelectionModal';
import { useCountry } from '../context/CountryContext';

const LandingPage = ({ onNavigateToChat }) => {
  const { country, availableCountries, updateCountry } = useCountry();
  const [showCountryModal, setShowCountryModal] = useState(false);

  const handleExploreAssistant = () => {
    // Always show country selection modal first
    setShowCountryModal(true);
  };

  const handleRequestDemo = () => {
    // Always show country selection modal first
    setShowCountryModal(true);
  };

  const handleCountrySelect = (selectedCountry) => {
    updateCountry(selectedCountry);
    // Close modal
    setShowCountryModal(false);
    // Navigate to chat after country selection
    if (onNavigateToChat) {
      onNavigateToChat();
    }
  };

  const handleModalClose = () => {
    // Only allow closing if country is already selected
    // Otherwise, user must select a country to proceed
    if (country) {
      setShowCountryModal(false);
    }
  };

  return (
    <>
      <CountrySelectionModal
        isOpen={showCountryModal}
        onClose={handleModalClose}
        onSelectCountry={handleCountrySelect}
        availableCountries={availableCountries}
        canClose={!!country}
      />
      <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <h1 className="hero-title">
            Turn your financial data into<br />strategic insight—<span className="highlight">instantly.</span>
          </h1>
          <p className="hero-description">
            The AI Earnings Assistant helps banking CFOs and FP&A teams automate analysis, explain variances, simulate
            impact, and prepare board-ready insights—faster and smarter than ever before.
          </p>
          <div className="hero-buttons">
            <button
              onClick={handleRequestDemo}
              className="btn btn-primary"
            >
              Request a Demo
            </button>
            <button
              onClick={handleExploreAssistant}
              className="btn btn-outline"
            >
              Explore the Assistant <span className="arrow">→</span>
            </button>
          </div>
        </div>
        <div className="hero-background"></div>
      </section>

      {/* Vision Statement Section */}
      <section className="vision-section">
        <div className="container">
          <div className="vision-header">
            <div className="target-icon">🎯</div>
            <h2 className="vision-title">Our Vision</h2>
          </div>
          <p className="vision-text">
            To become the CFO's trusted AI partner — transforming financial data into{' '}
            <span className="vision-highlight">clear insights</span>,{' '}
            <span className="vision-highlight">confident decisions</span>, and{' '}
            <span className="vision-highlight">boardroom-ready narratives</span>.
          </p>
        </div>
        <div className="vision-background"></div>
      </section>

      {/* Pain Points Section */}
      <section className="pain-points-section">
        <div className="container">
          <h2 className="section-title">
            Solving Your Toughest Financial Challenges
          </h2>
          <div className="pain-points-grid">
            <div className="pain-point-card">
              <div className="card-icon">💡</div>
              <h3 className="card-title">Manual, Fragmented Earnings Analysis</h3>
              <p className="card-description">
                CFOs and FP&A teams spend countless hours stitching together financial data from disparate systems.
                Our AI automates this, providing a unified view.
              </p>
            </div>
            <div className="pain-point-card">
              <div className="card-icon">📊</div>
              <h3 className="card-title">Peer Comparison Analysis</h3>
              <p className="card-description">
                Our AI provides instant peer comparisons across key metrics, helping you identify competitive
                positioning and improvement opportunities.
              </p>
            </div>
            <div className="pain-point-card">
              <div className="card-icon">💼</div>
              <h3 className="card-title">Time-Consuming Board & Regulator Reporting</h3>
              <p className="card-description">
                Preparing polished narratives and explanations is a burden. Automate report drafting and ensure
                consistency with AI-powered narratives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">
            Powerful Features for Financial Foresight
          </h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">📈</div>
              <div className="feature-content">
                <h3 className="feature-title">Earnings Dashboard</h3>
                <p className="feature-description">
                  Instant quarterly snapshots, KPI cards, and AI-powered summaries for a holistic view.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <div className="feature-content">
                <h3 className="feature-title">Variance Analysis</h3>
                <p className="feature-description">
                  Drill down into specific changes with AI-generated explanations and driver breakdowns.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💬</div>
              <div className="feature-content">
                <h3 className="feature-title">CFO Chat Assistant</h3>
                <p className="feature-description">
                  Ask natural language questions about financials and get instant, cited answers.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🧮</div>
              <div className="feature-content">
                <h3 className="feature-title">What-if Scenario Builder</h3>
                <p className="feature-description">
                  Simulate the impact of business levers on key KPIs with real-time projections.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📄</div>
              <div className="feature-content">
                <h3 className="feature-title">Board Deck Drafting</h3>
                <p className="feature-description">
                  Generate AI-powered narratives for board presentations with customizable tone.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <div className="feature-content">
                <h3 className="feature-title">Peer Comparison & Benchmarking</h3>
                <p className="feature-description">
                  Compare your bank's performance against industry peers with automated benchmarking and competitive
                  positioning insights.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Roadmap Section */}
      <section className="roadmap-section">
        <div className="container">
          <div className="roadmap-header">
            <div className="map-icon">📍</div>
            <h2 className="section-title">Product Roadmap</h2>
            <p className="roadmap-subtitle">
              Upcoming features designed to make CFOs even more effective and strategic
            </p>
          </div>
          <div className="roadmap-grid">
            <div className="roadmap-card">
              <div className="roadmap-icon">✅</div>
              <div className="roadmap-content">
                <h3 className="roadmap-title">Auto-Reconciliation & Integrity Checks</h3>
                <p className="roadmap-description">
                  Reduce reporting errors and data mismatch across sources with automated validation.
                </p>
              </div>
            </div>
            <div className="roadmap-card">
              <div className="roadmap-icon">⚠️</div>
              <div className="roadmap-content">
                <h3 className="roadmap-title">Proactive Alerts with Context</h3>
                <p className="roadmap-description">
                  Get notified about risks, variances, and key movements with intelligent context.
                </p>
              </div>
            </div>
            <div className="roadmap-card">
              <div className="roadmap-icon">🔍</div>
              <div className="roadmap-content">
                <h3 className="roadmap-title">Attribution Analysis (Causal Drivers)</h3>
                <p className="roadmap-description">
                  Know what exactly influenced earnings up/down with deep-dive causal analysis.
                </p>
              </div>
            </div>
            <div className="roadmap-card">
              <div className="roadmap-icon">📉</div>
              <div className="roadmap-content">
                <h3 className="roadmap-title">Forecast Accuracy Monitoring</h3>
                <p className="roadmap-description">
                  Build trust and improve planning discipline with continuous forecast monitoring.
                </p>
              </div>
            </div>
            <div className="roadmap-card">
              <div className="roadmap-icon">🌍</div>
              <div className="roadmap-content">
                <h3 className="roadmap-title">Macro & Regulatory Sentiment Integration</h3>
                <p className="roadmap-description">
                  Prepare early for external shocks with integrated macro-economic analysis.
                </p>
              </div>
            </div>
            <div className="roadmap-card">
              <div className="roadmap-icon">🗄️</div>
              <div className="roadmap-content">
                <h3 className="roadmap-title">Integration with CFO Tools (Excel/BI)</h3>
                <p className="roadmap-description">
                  Seamless transition from AI insights to boardroom tools with native integrations.
                </p>
              </div>
            </div>
          </div>
          <div className="roadmap-cta">
            <button
              onClick={handleRequestDemo}
              className="btn btn-outline"
            >
              Request Early Access <span className="arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">Ready to Transform Your Financial Insights?</h2>
          <p className="cta-description">
            Empower your team with AI-driven precision and foresight. Request a demo to see the AI Earnings Assistant in
            action.
          </p>
          <button
            onClick={handleRequestDemo}
            className="btn btn-white"
          >
            Request a Demo
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} AI Earnings Assistant. All rights reserved.</p>
          <p>Designed for Banking CFOs & FP&A Teams.</p>
        </div>
      </footer>
      </div>
    </>
  );
};

export default LandingPage;
